// Session management using localStorage
const SESSION_KEY = 'dripbattle_session';
const DAILY_FREE_TOKENS = 50; // Free daily Lit-Bits

export function initializeSession() {
  let session = getSession();

  if (!session) {
    session = {
      id: generateSessionId(),
      tokens: 0, // Will be synced from server
      votedOn: [], // Array of "swipe_avatarId" for Aura Farming tracking
      createdAt: Date.now(),
      lastDailyClaim: null, // Will be set by backend after claiming
      farmBoost: 0, // Remaining boosted swipes (2x Aura)
      powerVoteActive: false, // Single-use 3x vote multiplier
    };
    saveSession(session);
  } else {
    // Migrate old "votes" to "tokens" if needed
    if (session.votes !== undefined && session.tokens === undefined) {
      session.tokens = session.votes;
      delete session.votes;
      saveSession(session);
    }

    // Initialize new fields if missing
    if (!session.lastDailyClaim) {
      session.lastDailyClaim = null;
    }
    if (session.farmBoost === undefined) {
      session.farmBoost = 0;
    }
    if (!session.spotlightEndTime) {
      session.spotlightEndTime = null;
    }
    if (session.powerVoteActive === undefined) {
      session.powerVoteActive = false;
    }
  }

  return session;
}

// Sync session tokens with server balance
export async function syncTokensWithServer() {
  try {
    const { getServerBalance } = await import('./api.js');
    const result = await getServerBalance();

    if (result && result.balance !== undefined) {
      let session = getSession();

      // Create a temporary session if it doesn't exist (for JWT-authenticated users)
      if (!session) {
        session = {
          id: generateSessionId(),
          tokens: result.balance,
          votedOn: [],
          createdAt: Date.now(),
          lastDailyClaim: null,
          farmBoost: 0,
          powerVoteActive: false,
        };
      } else {
        session.tokens = result.balance;
      }

      saveSession(session);
      return result.balance;
    }
  } catch (error) {
    console.error('Failed to sync with server:', error);
  }
  return null;
}

// Claim daily free tokens from backend (checks IP and VPN)
export function claimDailyTokens(tokensEarned) {
  const session = getSession();
  if (!session) return;

  session.tokens = (session.tokens || 0) + tokensEarned;
  session.lastDailyClaim = getTodayUTC();
  saveSession(session);
  return session;
}

// Get today's date in YYYY-MM-DD format (UTC)
function getTodayUTC() {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

export function getSession() {
  try {
    const data = localStorage.getItem(SESSION_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to get session:', error);
    return null;
  }
}

export function saveSession(session) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Failed to save session:', error);
  }
}

export function updateSessionTokens(newTokenCount, votedOnKey = null) {
  const session = getSession();
  if (!session) return;

  session.tokens = newTokenCount;

  // Only track voted on for Aura Farming (swipe mode)
  if (votedOnKey && !session.votedOn.includes(votedOnKey)) {
    session.votedOn.push(votedOnKey);
  }

  saveSession(session);
}

export function addTokens(count) {
  const session = getSession();
  if (!session) return;

  session.tokens = (session.tokens || 0) + count;
  saveSession(session);
}

export function spendTokens(count) {
  const session = getSession();
  if (!session) return false;

  if (session.tokens >= count) {
    session.tokens -= count;
    saveSession(session);
    return true;
  }
  return false;
}

// Activate Farm Boost (20 Lit-Bits for 2x Aura on next 20 swipes)
export function activateFarmBoost() {
  const session = getSession();
  if (!session || session.tokens < 20) return false;

  session.tokens -= 20;
  session.farmBoost = 20;
  saveSession(session);
  return true;
}

// Use one Farm Boost charge (returns true if boost active)
export function useFarmBoost() {
  const session = getSession();
  if (!session || session.farmBoost <= 0) return false;

  session.farmBoost -= 1;
  saveSession(session);
  return true;
}

// Check if Farm Boost is active
export function hasFarmBoost() {
  const session = getSession();
  return session && session.farmBoost > 0;
}

// Activate Power Vote (12 Lit-Bits for 3x vote impact on next vote)
export function activatePowerVote() {
  const session = getSession();
  if (!session || session.tokens < 12) return false;

  session.tokens -= 12;
  session.powerVoteActive = true;
  saveSession(session);
  return true;
}

// Use Power Vote (deactivate after use)
export function usePowerVote() {
  const session = getSession();
  if (!session || !session.powerVoteActive) return false;

  session.powerVoteActive = false;
  saveSession(session);
  return true;
}

// Check if Power Vote is active
export function hasPowerVote() {
  const session = getSession();
  return session && session.powerVoteActive === true;
}

function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Check if voted on specific avatar/category combo
export function hasVoted(avatarId, categoryId) {
  const session = getSession();
  if (!session) return false;

  const voteKey = `${avatarId}_${categoryId}`;
  return session.votedOn.includes(voteKey);
}
